import { useTranslation } from 'react-i18next';
import { LOCAL_AR, LOCAL_EN, ROUTE } from '@/config';
import { message } from 'antd';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import type { MenuInfo } from 'rc-menu/lib/interface';
import { Bell, Globe, ArrowLeft, Ticket, CheckCircle2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import useLanguageStore, { LanguageType } from '@/stores/useLanguageStore';
import { ProgressBarToast } from './ProgressBarToast';
import NotificationDrawer from './NotificationDrawer';
import { formatDateOrRelative, getPageTitleWithPathAndHash } from '@/utils';
import { OrderPageHeader } from './OrderPageHeader';
import FinancePageHeader from './FinancePageHeader/FinancePageHeader';
import FinanceDetailPageHeader from './FinanceDetailPageHeader/FinanceDetailPageHeader';
import PickupDetailPageHeader from './PickupDetailPageHeader/PickupDetailPageHeader';
import { usePollingMechanismStore } from '@/stores';
import { useFetchNotificationsCount, useFetchTicketById, useUpdateTicket } from '@/hooksApi';
import { useQueryClient } from '@tanstack/react-query';
import { TicketPageHeader } from './TicketPageHeader';
import DoubleArrowsReload from '../Icons/DoubleArrowsReload';
// TODO: Zendesk temporarily disabled due to migration.
// Replace with new chat provider when ready.
// import useZendesk from '@/hooks/useZendesk';
import usePermission from '@/hooks/usePermission';
import { PermissionName } from '@/types';
import { GlobalSearch } from '@/components/GlobalSearch/GlobalSearch';

const Header = () => {
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [pageTitle, setPageTitle] = useState('');

  const location = useLocation();
  const navigate = useNavigate();

  const ticketDetailsPathData = matchPath(ROUTE.TICKET_CENTER_DETAILS, location.pathname);

  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { setLanguage, language } = useLanguageStore();
  // TODO: Zendesk temporarily disabled due to migration.
  // const { showZendeskChat } = useZendesk();
  const showZendeskChat = () => {};
  const { hasPermission } = usePermission();

  const { processes, stopPolling } = usePollingMechanismStore();

  // Only fetch notification count if user has notification permission
  const { data: notificationCount } = useFetchNotificationsCount(
    hasPermission(PermissionName.NOTIFICATION),
  );

  const ticketId = ticketDetailsPathData?.params.id;

  const { data: ticketData, isFetching: isFetchingTicket } = useFetchTicketById(
    ticketId as string,
    language,
    {
      enabled: !!ticketDetailsPathData,
      retry: false,
    },
  );

  const { mutate: updateTicket, isLoading: isUpdatingTicket } = useUpdateTicket();

  const ticketRawStatus = ticketData?.object?.status?.toLowerCase() ?? 'open';
  const isTicketSolved = ['resolve', 'resolved', 'solved', 'closed'].includes(ticketRawStatus);

  const handleMarkAsSolved = () => {
    if (!ticketId) return;
    updateTicket(
      { status: 'solved' as any, id: ticketId },
      {
        onSuccess: () => {
          message.success(String(t('operation_completed_successfully')));
          queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
        },
      },
    );
  };

  // Update page title based on location path and hash
  useEffect(() => {
    const pathname = location.pathname.startsWith('/')
      ? location.pathname.slice(1)
      : location.pathname;

    const title = getPageTitleWithPathAndHash(pathname, location.hash);

    setPageTitle(title);
    queryClient.invalidateQueries(['notification-count']);
  }, [location.pathname, location.hash]);

  const toggleLanguage = (e: MenuInfo) => {
    const newLanguage = e.key as LanguageType;

    setLanguage(newLanguage);
  };

  // Language menu with English and Arabic options
  const languageMenu = {
    items: [
      { key: LOCAL_EN, label: 'English' },
      { key: LOCAL_AR, label: 'العربية' },
    ],
    onClick: toggleLanguage,
  };

  const isFinanceDetailPage = /^\/finance\/details\/[^/]+$/.test(location.pathname);
  const isPickupDetailPage = /^\/pickups\/details\/[^/]+$/.test(location.pathname);
  const isRTODetailPage = /^\/pickups\/rto\/[^/]+$/.test(location.pathname);
  const isClaimDetailPage = /^\/claims\/[^/]+$/.test(location.pathname);
  const isOrderDetailPage = /^\/orders\/(?!add|edit|rto)([^/]+)$/.test(location.pathname);
  const isTicketDetailPage = /^\/ticket-center\/details\/[^/]+$/.test(location.pathname);

  const renderCustomHeader = () => {
    // Detail pages render their own action buttons in the sub-header area,
    // not in the main header. Only list pages get header actions.
    if (isFinanceDetailPage || isPickupDetailPage || isRTODetailPage) {
      return null;
    }

    // Ticket detail page — "Mark as Solved" / "Reopen" button in header
    if (isTicketDetailPage && ticketData?.object && hasPermission(PermissionName.SOLVE_TICKET)) {
      if (!isTicketSolved) {
        return (
          <button
            onClick={handleMarkAsSolved}
            disabled={isUpdatingTicket}
            className='flex items-center gap-1.5 h-9 px-4 rounded-lg border border-solid border-sd-border bg-sd-card text-[13px] font-semibold hover:bg-sd-muted transition-colors cursor-pointer disabled:opacity-50'
          >
            <CheckCircle2 className='w-4 h-4' strokeWidth={1.5} />
            {String(t('mark_as_solved', { defaultValue: 'Mark as Solved' }))}
          </button>
        );
      }
      // BOXY-249 — no Reopen button here. It PATCHed status to open on click,
      // violating R7: reopening is tied to COMMENT SUBMISSION, not a tap. The
      // composer on the detail page is the reopen path (it stays enabled on
      // solved/resolved tickets); a status flip with no message gave agents a
      // reopened ticket with nothing new to act on.
      return null;
    }

    switch (location.pathname) {
      case ROUTE.ORDERS:
        return <OrderPageHeader />;

      case ROUTE.TICKET_CENTER:
        return <TicketPageHeader />;

      case ROUTE.FINANCE:
        return <FinancePageHeader />;

      default:
        break;
    }
  };

  const invalidateTicketApis = () => {
    queryClient.invalidateQueries(['ticket', ticketId, language]);
    queryClient.invalidateQueries(['ticket-comments', ticketId]);
  };

  // Order detail pages now use the same layout header as other pages.
  // CTA buttons (Ticket, Ship Now, etc.) are rendered in the page's sub-header area.

  // The pickups list page (/pickups) renders its own content header (pill tabs, info bar, table).
  // Suppress only the global page title <h1> on that route so the header bar chrome
  // (search, notifications, language switcher) remains fully visible.
  // Finance page now renders its own "Finance" title inside the header — keep the global <h1>.
  const isPickupsListPage = location.pathname === ROUTE.PICKUPS;
  const isTicketListPage = location.pathname === ROUTE.TICKET_CENTER;

  // Order detail pages now use the same layout header as other pages.
  // CTA buttons (Ticket, Ship Now, etc.) are rendered in the page's sub-header area.

  return (
    <div className='sd-scope sd-font flex flex-col'>
      {/* Main Header Row */}
      <header className='flex items-center justify-between border-b border-sd-border px-4 sm:px-10 py-4'>
        {/* Left: Back button (finance detail) or Page Title */}
        {/* Suppressed on the pickups list page — the page renders its own content header */}
        {isFinanceDetailPage ? (
          <div className='flex items-center gap-2 shrink-0'>
            <button
              onClick={() => navigate(-1)}
              className='rounded-full p-2 transition-colors hover:bg-sd-muted cursor-pointer'
              aria-label={String(t('back'))}
            >
              <ArrowLeft className='h-4 w-4 text-sd-muted-foreground rtl:rotate-180' />
            </button>
            <h1 className='text-xl font-bold tracking-tight text-sd-foreground'>
              {String(t('remittance', { defaultValue: 'Remittance' }))}
            </h1>
          </div>
        ) : (isPickupDetailPage || isRTODetailPage) ? (
          <div className='flex items-center gap-2 shrink-0'>
            <button
              onClick={() => navigate(-1)}
              className='rounded-full p-2 transition-colors hover:bg-sd-muted cursor-pointer'
              aria-label={String(t('back'))}
            >
              <ArrowLeft className='h-4 w-4 text-sd-muted-foreground rtl:rotate-180' />
            </button>
            <h1 className='text-xl font-bold tracking-tight text-sd-foreground'>
              {String(t('pickups_and_returns', { defaultValue: 'Pickups & Returns' }))}
            </h1>
          </div>
        ) : isClaimDetailPage ? (
          <div className='flex items-center gap-2 shrink-0'>
            <button
              onClick={() => navigate(-1)}
              className='rounded-full p-2 transition-colors hover:bg-sd-muted cursor-pointer'
              aria-label={String(t('back'))}
            >
              <ArrowLeft className='h-4 w-4 text-sd-muted-foreground rtl:rotate-180' />
            </button>
            <h1 className='text-xl font-bold tracking-tight text-sd-foreground'>
              {String(t('claims', { defaultValue: 'Claims' }))}
            </h1>
          </div>
        ) : isTicketDetailPage ? (
          <div className='flex items-center gap-2 shrink-0'>
            <button
              onClick={() => {
                // QA-5: Preserve ticket list page number when going back
                const prevPage = (location.state as any)?.prevPage;
                if (prevPage) {
                  navigate(prevPage);
                } else {
                  navigate(-1);
                }
              }}
              className='rounded-full p-2 transition-colors hover:bg-sd-muted cursor-pointer'
              aria-label={String(t('back'))}
            >
              <ArrowLeft className='h-4 w-4 text-sd-muted-foreground rtl:rotate-180' />
            </button>
            <h1 className='text-xl font-bold tracking-tight text-sd-foreground'>
              {String(t('ticket_center', { defaultValue: 'Ticket Center' }))}
            </h1>
          </div>
        ) : isOrderDetailPage ? (
          <div className='flex items-center gap-2 shrink-0'>
            <button
              onClick={() => navigate(-1)}
              className='rounded-full p-2 transition-colors hover:bg-sd-muted cursor-pointer'
              aria-label={String(t('back'))}
            >
              <ArrowLeft className='h-4 w-4 text-sd-muted-foreground rtl:rotate-180' />
            </button>
            <h1 className='text-xl font-bold tracking-tight text-sd-foreground'>
              {String(t('orders', { defaultValue: 'Orders' }))}
            </h1>
          </div>
        ) : isTicketListPage ? (
          <div className='flex items-center gap-2.5 shrink-0'>
            <Ticket className='w-5 h-5 text-sd-muted-foreground opacity-70' strokeWidth={1.5} />
            <h1 className='text-xl font-bold tracking-tight text-sd-foreground'>
              {String(t('ticket_center', { defaultValue: 'Ticket Center' }))}
            </h1>
          </div>
        ) : pageTitle && pageTitle !== 'Unknown' ? (
          <h1 className='text-lg sm:text-2xl font-bold tracking-tight text-sd-foreground'>
            {t(pageTitle)}
          </h1>
        ) : (
          <div /> /* Spacer */
        )}

        {/* Center: Global Search */}
        <div className='mx-4 hidden flex-1 sm:flex sm:max-w-[480px]'>
          <GlobalSearch />
        </div>

        {/* Right: Page Actions + Icons */}
        <div className='flex items-center gap-2 sm:gap-3'>
          {/* Page-specific action buttons (e.g. Orders: Export/Import/New Order) */}
          {renderCustomHeader()}

          {/* Notification Icon */}
          <button
            disabled={!hasPermission(PermissionName.NOTIFICATION)}
            onClick={() => setDrawerVisible(true)}
            className='relative rounded-full p-2 transition-colors hover:bg-sd-muted disabled:opacity-50 disabled:cursor-not-allowed'
          >
            <Bell className='h-5 w-5 text-sd-muted-foreground' strokeWidth={1.5} />
            {(notificationCount?.object?.overall_unseen_count ?? 0) > 0 && (
              <span className='absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-sd-destructive' />
            )}
          </button>

          {/* Language Icon */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className='rounded-full p-2 transition-colors hover:bg-sd-muted'>
                <Globe className='h-5 w-5 text-sd-muted-foreground' strokeWidth={1.5} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              <DropdownMenuItem onClick={() => toggleLanguage({ key: LOCAL_EN } as MenuInfo)}>
                English
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => toggleLanguage({ key: LOCAL_AR } as MenuInfo)}>
                العربية
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Secondary Row: Ticket Badge */}
      {!!ticketDetailsPathData && (
        <div className='flex items-center justify-between px-4 sm:px-10 py-3'>
          {/* Ticket "Last Updated" Badge */}
          {!!ticketDetailsPathData && ticketData?.object.updated_at && (
            <div className='flex items-center gap-2 px-3 py-1.5 rounded-lg bg-sd-muted'>
              <span className='text-xs font-normal text-sd-muted-foreground'>
                {t('last_updated').toUpperCase()} -{' '}
                {formatDateOrRelative(ticketData?.object.updated_at)}
              </span>
              <DoubleArrowsReload
                className={`cursor-pointer transition-transform ${isFetchingTicket ? 'animate-spin-reverse' : ''}`}
                color='#38383E'
                width={16}
                height={16}
                onClick={invalidateTicketApis}
              />
            </div>
          )}

          {/* Custom Page Headers moved to main header row */}
        </div>
      )}

      {/* Progress Bar Toasts */}
      {processes.size > 0 && (
        <div className='flex gap-2 px-4 sm:px-10 py-2'>
          {Array.from(processes.entries()).map(([processName, toastData], index) => (
            <ProgressBarToast
              key={index}
              progressToastData={toastData}
              processName={processName}
              removeToast={stopPolling}
            />
          ))}
        </div>
      )}

      {/* Notification Drawer */}
      <NotificationDrawer
        notificationCount={notificationCount?.object}
        drawerVisible={drawerVisible}
        setDrawerVisible={setDrawerVisible}
      />
    </div>
  );
};

export default Header;
